import { Node, mergeAttributes } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";

export const MentionNode = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-key"),
        renderHTML: (attributes) => ({
          "data-key": attributes.key,
        }),
      },
      displayKey: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-display-key"),
        renderHTML: (attributes) => ({
          "data-display-key": attributes.displayKey || attributes.key,
        }),
      },
      label: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attributes) => ({
          "data-label": attributes.label,
        }),
      },
      participant_type_code: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-participant_type_code"),
        renderHTML: (attributes) => ({
          "data-participant_type_code": attributes.participant_type_code,
        }),
      },
      service_code: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-service_code"),
        renderHTML: (attributes) => ({
          "data-service_code": attributes.service_code,
        }),
      },
      value: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-value"),
        renderHTML: (attrs) => ({
          "data-value": attrs.value,
        }),
      },
      isCustom: {
        default: false,
        parseHTML: (element) => {
          const value = element.getAttribute("data-is-custom");
          return value === "true" || value === "";
        },
        renderHTML: (attributes) => ({
          "data-is-custom": attributes.isCustom ? "true" : null,
        }),
      },

      isNotary: {
        default: false,
        parseHTML: (element) => {
          const value = element.getAttribute("data-is-notary");
          return value === "true" || value === "";
        },
        renderHTML: (attributes) => ({
          "data-is-notary": attributes.isNotary ? "true" : null,
        }),
      },

      isNumberToWords: {
        default: false,
        parseHTML: (element) => {
          const value = element.getAttribute("data-is-number-to-words");
          return value === "true" || value === "";
        },
        renderHTML: (attributes) => ({
          "data-is-number-to-words": attributes.isNumberToWords ? "true" : null,
        }),
      },

      isLoading: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute("data-is-loading") === "true",
        renderHTML: (attributes) => ({
          "data-is-loading": attributes.isLoading ? "true" : null,
        }),
      },
    };
  },

  addOptions() {
    return {
      mode: "edit", // edit | view | preview
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-mention]",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const mode = this.options.mode;
    const value = node.attrs.value?.trim();

    // Получаем атрибуты для классов
    const isCustom = node.attrs.isCustom;
    const isNotary = node.attrs.isNotary;
    const isNumberToWords = node.attrs.isNumberToWords;

    // =========================
    // PREVIEW MODE
    // =========================
    if (mode === "preview") {
      if (!value) {
        return ["span", { "data-mention": "", style: "display:none" }];
      }

      // Для preview режима также добавляем классы
      const previewClasses = ["mention-preview"];
      if (isCustom) previewClasses.push("mention-custom");
      if (isNotary) previewClasses.push("mention-notary");
      if (isNumberToWords) previewClasses.push("mention-calc");

      return [
        "span",
        mergeAttributes(HTMLAttributes, {
          "data-mention": "",
          class: previewClasses.join(" "),
        }),
        value,
      ];
    }

    const displayText =
      node.attrs.value && node.attrs.value.trim() !== ""
        ? node.attrs.value
        : node.attrs.displayKey
        ? `$${node.attrs.displayKey}`
        : `$${node.attrs.key}`;

    // Определяем классы на основе атрибутов
    const mentionClasses = ["mention-node"];
    if (isCustom) mentionClasses.push("mention-custom");
    if (isNotary) mentionClasses.push("mention-notary");
    if (isNumberToWords) mentionClasses.push("mention-calc");
    if (node.attrs.isLoading) mentionClasses.push("mention-skeleton");

    const titleAttr = {
      title: node.attrs.label || node.attrs.key,
    };

    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-mention": "",
        class: mentionClasses.join(" "),
        contenteditable: "false",
        style: "user-select: none;",
        ...titleAttr,
      }),
      `${displayText}`,
    ];
  },

  // ========================
  // TEXT EXPORT (IMPORTANT)
  // ========================
  renderText({ node }) {
    const mode = this.options.mode;

    if (mode === "preview") {
      return node.attrs.value || "";
    }
    return node.attrs.displayKey || `${node.attrs.key}`;
  },

  // ========================
  // COMMANDS
  // ========================
  addCommands() {
    return {
      insertMention:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              key: attrs.key,
              label: attrs.label,
              service_code: attrs.service_code,
              displayKey: attrs.displayKey || `${attrs.key}`,
              participant_type_code: attrs?.participant_type_code,
              isCustom: attrs?.isCustom || false,
              isNotary: attrs?.isNotary || false,
              isNumberToWords: attrs?.isNumberToWords || false,
            },
          });
        },

      insertCustomMention:
        (attrs) =>
        ({ commands }) => {
          // Insert the mention
          commands.insertContent({
            type: this.name,
            attrs: {
              ...attrs,
              key: attrs.key,
              label: attrs.label,
              displayKey: `${attrs.displayKey}`,
              isCustom: true,
              isNotary: attrs?.isNotary || false,
              isNumberToWords: attrs?.isNumberToWords || false,
            },
          });

          // Insert a space after the mention
          commands.insertContent({
            type: "text",
            text: " ",
          });

          return true; // usually commands return boolean
        },
    };
  },
});

export const MentionSuggestion = ({ items = [] }) =>
  Suggestion({
    char: "@",
    startOfLine: false,
    allowSpaces: false,

    items: ({ query }) => {
      if (!query) return items;

      const q = query.toLowerCase();

      return items.filter(
        (item) =>
          item.key.toLowerCase().includes(q) ||
          item.label.toLowerCase().includes(q)
      );
    },

    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: "mention",
            attrs: {
              key: props.key,
              label: props.label,
              displayKey: `${props.displayKey}`,
              participant_type_code: props.participant_type_code,
              service_code: props.service_code,
              isCustom: props.isCustom || false,
              isNotary: props.isNotary || false,
            },
          },
          { type: "text", text: " " },
        ])
        .run();
    },

    render: () => {
      let popup;
      let selectedIndex = 0;
      let currentItems = [];

      const createItem = (item, index, command) => {
        const el = document.createElement("div");

        el.className = "mention-suggestion-item";
        el.textContent = `${item.label} — $${item.key}`;

        if (index === selectedIndex) {
          el.classList.add("is-active");
        }

        el.onclick = () => command(item);

        return el;
      };

      return {
        onStart: (props) => {
          currentItems = props.items;
          selectedIndex = 0;

          popup = document.createElement("div");
          popup.className = "mention-suggestion-popup";

          currentItems.forEach((item, i) =>
            popup.appendChild(createItem(item, i, props.command))
          );

          document.body.appendChild(popup);
          this.updatePosition(popup, props.clientRect);
        },

        onUpdate: (props) => {
          currentItems = props.items;
          selectedIndex = 0;

          popup.innerHTML = "";
          currentItems.forEach((item, i) =>
            popup.appendChild(createItem(item, i, props.command))
          );

          this.updatePosition(popup, props.clientRect);
        },

        onKeyDown: (props) => {
          if (!popup) return false;

          if (props.event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % currentItems.length;
            this.refreshActive(popup);
            return true;
          }

          if (props.event.key === "ArrowUp") {
            selectedIndex =
              (selectedIndex - 1 + currentItems.length) % currentItems.length;
            this.refreshActive(popup);
            return true;
          }

          if (props.event.key === "Enter") {
            props.command(currentItems[selectedIndex]);
            popup.remove();
            return true;
          }

          if (props.event.key === "Escape") {
            popup.remove();
            return true;
          }

          return false;
        },

        onExit: () => {
          popup?.remove();
          popup = null;
        },

        updatePosition: (popup, clientRect) => {
          const rect = clientRect?.();
          if (!rect) return;

          popup.style.top = `${rect.bottom + window.scrollY}px`;
          popup.style.left = `${rect.left + window.scrollX}px`;
        },

        refreshActive: (popup) => {
          Array.from(popup.children).forEach((el, i) => {
            el.classList.toggle("is-active", i === selectedIndex);
          });
        },
      };
    },
  });
